/**
 * Sidebar — Collapsible left navigation.
 * v4.8 Spec Section 3: 11 groups, icon-only when collapsed,
 * user-reorderable (future), favourites/recent in footer.
 *
 * Apple-inspired glassmorphism. Light AND dark mode.
 */

import { useState, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useFeatureFlags } from '@/hooks/services';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';
import { NAVIGATION, type NavGroup, type NavItem } from './navigation';
import { NavIcon } from './NavIcon';

const ROLE_HIERARCHY: Record<string, number> = {
  director: 4,
  manager: 3,
  coordinator: 2,
  viewer: 1,
};

export function Sidebar(): React.ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(NAVIGATION.map((g) => g.key))
  );
  const { profile } = useAuthContext();
  const { isEnabled } = useFeatureFlags();
  const { resolvedTheme, toggle: toggleTheme } = useTheme();

  const userRoleLevel = ROLE_HIERARCHY[profile?.role ?? 'viewer'] ?? 1;

  /** Filter nav items by feature flags and role */
  const isItemVisible = useCallback(
    (item: NavItem): boolean => {
      // Feature flag check
      if (item.featureFlag && !isEnabled(item.featureFlag)) return false;
      // Role check
      if (item.minRole) {
        const requiredLevel = ROLE_HIERARCHY[item.minRole] ?? 0;
        if (userRoleLevel < requiredLevel) return false;
      }
      return true;
    },
    [isEnabled, userRoleLevel]
  );

  /** Filtered navigation — only visible groups with visible items */
  const visibleNavigation = useMemo(
    (): NavGroup[] =>
      NAVIGATION.map((group) => ({
        ...group,
        items: group.items.filter(isItemVisible),
      })).filter((group) => group.items.length > 0),
    [isItemVisible]
  );

  const toggleGroup = useCallback((groupKey: string): void => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  return (
    <aside
      className={`
        hidden md:flex flex-col glass-sidebar h-screen sticky top-0 z-40
        transition-all duration-normal overflow-hidden
        ${collapsed ? 'w-[68px]' : 'w-[260px]'}
      `}
    >
      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-4 min-h-[56px]">
        {!collapsed && (
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="text-sm font-bold text-text-primary dark:text-text-dark-primary truncate">
              Lacey Group
            </span>
            <span className="text-xs text-gold font-medium">CMD</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="touch-target text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25" />
            )}
          </svg>
        </button>
      </div>

      <div className="h-px bg-border-light dark:bg-border-dark mx-3" />

      {/* ─── Navigation groups ─────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 scrollbar-hide">
        {visibleNavigation.map((group) => (
          <div key={group.key} className="mb-1">
            {/* Group label */}
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center justify-between px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-secondary/60 dark:text-text-dark-secondary/60 hover:text-text-secondary dark:hover:text-text-dark-secondary transition-colors"
              >
                {group.label}
                <svg
                  className={`w-3 h-3 transition-transform duration-fast ${
                    expandedGroups.has(group.key) ? '' : '-rotate-90'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
            )}

            {/* Group items */}
            {(collapsed || expandedGroups.has(group.key)) && (
              <div className="space-y-0.5 px-2">
                {group.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-2 py-2 rounded-button transition-all duration-fast
                      ${collapsed ? 'justify-center' : ''}
                      ${
                        isActive
                          ? 'bg-gold/10 text-gold dark:bg-gold/15 dark:text-gold-400 font-medium'
                          : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary hover:bg-black/5 dark:hover:bg-white/5'
                      }
                    `}
                    title={collapsed ? item.label : undefined}
                  >
                    <NavIcon name={item.icon} className="w-[18px] h-[18px] flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-[13px] truncate">{item.label}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* ─── Footer ────────────────────────────────────────────── */}
      <div className="border-t border-border-light dark:border-border-dark px-2 py-3 space-y-1">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className={`
            flex items-center gap-3 px-2 py-2 rounded-button w-full
            text-text-secondary dark:text-text-dark-secondary
            hover:text-text-primary dark:hover:text-text-dark-primary
            hover:bg-black/5 dark:hover:bg-white/5
            transition-all duration-fast
            ${collapsed ? 'justify-center' : ''}
          `}
          title={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} mode`}
        >
          {resolvedTheme === 'light' ? (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            </svg>
          ) : (
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            </svg>
          )}
          {!collapsed && (
            <span className="text-[13px]">
              {resolvedTheme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </span>
          )}
        </button>

        {/* User profile */}
        {profile && (
          <div
            className={`
              flex items-center gap-3 px-2 py-2 rounded-button
              ${collapsed ? 'justify-center' : ''}
            `}
          >
            <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-gold">
                {profile.full_name.charAt(0).toUpperCase()}
              </span>
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <p className="text-[13px] font-medium text-text-primary dark:text-text-dark-primary truncate">
                  {profile.full_name}
                </p>
                <p className="text-[11px] text-text-secondary dark:text-text-dark-secondary capitalize">
                  {profile.role}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
